import { useCallback, useEffect, useEffectEvent, useState } from 'react';

export interface WebSocketRequestOptions<TMessage> {
  // Full WebSocket-URL (wss://…)
  url: string;
  // Avgjør om en melding fra backend er den siste før vi lukker tilkoblingen
  isFinalMessage: (message: TMessage) => boolean;
  // Kalles for hver melding fra backend
  onMessage: (message: TMessage) => void;
  // Kalles når tilkoblingen lukkes eller feiler
  onAborted: (event?: CloseEvent) => void;
  // Maks ventetid fra start() til siste melding
  timeoutMs?: number;
}

export interface WebSocketRequestHandle<TRequest> {
  // Send data til backend
  start: (request: TRequest) => void;
  // Avbryter pågående request
  cancel: () => void;
}

/**
 * Hent data fra WebSocket. Laget for å åpne websocketen først når start() kalles, for så å vente på meldinger inntil socketen lukkes.
 * Hvis start() kalles på nytt før socketen lukkes, lukkes den gamle og en ny åpnes.
 *
 * Ikke egnet for å sende mer enn én melding til backend.
 */
export const useWebSocketRequest = <TRequest, TMessage>(options: WebSocketRequestOptions<TMessage>): WebSocketRequestHandle<TRequest> => {
  const [activeRequest, setActiveRequest] = useState<{ request: TRequest } | null>(null);
  const onMessage = useEffectEvent(options.onMessage);
  const onAborted = useEffectEvent(options.onAborted);
  const isFinalMessage = useEffectEvent(options.isFinalMessage);
  const getConnectionConfig = useEffectEvent(() => ({ url: options.url, timeoutMs: options.timeoutMs }));

  useEffect(() => {
    if (!activeRequest) {
      return;
    }

    const { url, timeoutMs } = getConnectionConfig();

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      onAborted();
      return;
    }

    let settled = false;

    const timeoutId = timeoutMs ? window.setTimeout(() => socket.close(), timeoutMs) : undefined;

    socket.onopen = (): void => socket.send(JSON.stringify(activeRequest.request));

    socket.onmessage = (event: MessageEvent): void => {
      if (settled || typeof event.data !== 'string') {
        return;
      }

      let message: TMessage;
      try {
        message = JSON.parse(event.data) as TMessage;
      } catch {
        return;
      }

      if (isFinalMessage(message)) {
        settled = true;
        socket.close();
      }

      onMessage(message);
    };

    socket.onclose = (event: CloseEvent): void => {
      window.clearTimeout(timeoutId);
      if (settled) {
        return;
      }
      settled = true;
      onAborted(event);
    };

    return (): void => {
      settled = true;
      window.clearTimeout(timeoutId);
      socket.close();
    };
  }, [activeRequest]);

  const start = useCallback((request: TRequest): void => setActiveRequest({ request }), []);

  const cancel = useCallback((): void => setActiveRequest(null), []);

  return { start, cancel };
};
